import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'styles/theme';
import { EMPTY_VALUE_KEY, facetedMapToKeys } from './core/excelColumnFilter';
import {
  HIGHLIGHT_PALETTE,
  inferColumnKind,
  operatorsForKind,
  OP_LABEL,
} from './core/gridConditionalRules';

const PANEL_WIDTH = 280;
const PANEL_MAX_HEIGHT = 440;

/**
 * 엑셀 Autofilter — 값 체크리스트 + 조건부(비교·색상·유지)
 */
const ExcelColumnFilterButton = ({
  column,
  iconTone = 'default',
  conditionRule = null,
  onApplyCondition,
  onClearCondition,
}) => {
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('list');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [search, setSearch] = useState('');
  const [draftSelected, setDraftSelected] = useState(() => new Set());
  const kind = inferColumnKind(column.columnDef?.meta);
  const ops = operatorsForKind(kind);
  const [draftOp, setDraftOp] = useState(conditionRule?.op || ops[0]);
  const [draftValue, setDraftValue] = useState(conditionRule?.value || '');
  const [draftValue2, setDraftValue2] = useState(conditionRule?.value2 || '');
  const [draftHide, setDraftHide] = useState(conditionRule?.hide !== false);
  const [draftCellColor, setDraftCellColor] = useState(conditionRule?.cellColor || null);
  const [draftRowColor, setDraftRowColor] = useState(conditionRule?.rowColor || null);
  const [draftPersist, setDraftPersist] = useState(Boolean(conditionRule?.persist));

  const hasCondition = Boolean(conditionRule?.op);
  const isFiltered = column.getIsFiltered() || hasCondition;

  const uniqueKeys = useMemo(() => {
    if (!open) return [];
    return facetedMapToKeys(column.getFacetedUniqueValues?.());
  }, [column, open]);

  const visibleKeys = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return uniqueKeys;
    return uniqueKeys.filter((k) => k.toLowerCase().includes(q));
  }, [uniqueKeys, search]);

  const resetDraftCondition = useCallback(() => {
    const nextOps = operatorsForKind(inferColumnKind(column.columnDef?.meta));
    setDraftOp(conditionRule?.op || nextOps[0]);
    setDraftValue(conditionRule?.value || '');
    setDraftValue2(conditionRule?.value2 || '');
    setDraftHide(conditionRule ? Boolean(conditionRule.hide) : true);
    setDraftCellColor(conditionRule?.cellColor || null);
    setDraftRowColor(conditionRule?.rowColor || null);
    setDraftPersist(Boolean(conditionRule?.persist));
  }, [column, conditionRule]);

  const openPanel = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;

      const current = column.getFilterValue();
      const selectedArr = current?.selected;
      let next;
      if (Array.isArray(selectedArr)) {
        next = new Set(selectedArr);
      } else {
        next = new Set(facetedMapToKeys(column.getFacetedUniqueValues?.()));
      }
      setDraftSelected(next);
      setSearch('');
      setTab(hasCondition ? 'cond' : 'list');
      resetDraftCondition();

      let left = rect.left;
      let top = rect.bottom + 2;
      if (left + PANEL_WIDTH > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - PANEL_WIDTH - 8);
      }
      if (top + PANEL_MAX_HEIGHT > window.innerHeight - 8) {
        top = Math.max(8, rect.top - PANEL_MAX_HEIGHT - 2);
      }
      setPos({ top, left });
      setOpen(true);
    },
    [column, hasCondition, resetDraftCondition]
  );

  const closePanel = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev) => {
      const t = ev.target;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      closePanel();
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') closePanel();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closePanel]);

  const toggleOne = (key) => {
    setDraftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    setDraftSelected((prev) => {
      const next = new Set(prev);
      visibleKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const clearVisible = () => {
    setDraftSelected((prev) => {
      const next = new Set(prev);
      visibleKeys.forEach((k) => next.delete(k));
      return next;
    });
  };

  const applyList = () => {
    const allKeys = facetedMapToKeys(column.getFacetedUniqueValues?.());
    const selected = [...draftSelected];
    const allSelected =
      allKeys.length > 0 &&
      selected.length === allKeys.length &&
      allKeys.every((k) => draftSelected.has(k));

    if (allSelected || (selected.length === 0 && allKeys.length === 0)) {
      column.setFilterValue(undefined);
    } else if (selected.length === 0) {
      column.setFilterValue({ selected: [] });
    } else {
      column.setFilterValue({ selected });
    }
    closePanel();
  };

  const applyCondition = () => {
    if (!draftHide && !draftCellColor && !draftRowColor) {
      return;
    }
    const needsValue = draftOp !== 'empty' && draftOp !== 'notEmpty';
    if (needsValue && String(draftValue || '').trim() === '') return;
    if (draftOp === 'between' && String(draftValue2 || '').trim() === '') return;
    onApplyCondition?.({
      op: draftOp,
      value: draftValue,
      value2: draftValue2,
      hide: draftHide,
      cellColor: draftCellColor,
      rowColor: draftRowColor,
      persist: draftPersist,
    });
    closePanel();
  };

  const clearFilter = () => {
    column.setFilterValue(undefined);
    onClearCondition?.();
    closePanel();
  };

  const allVisibleChecked =
    visibleKeys.length > 0 && visibleKeys.every((k) => draftSelected.has(k));
  const someVisibleChecked = visibleKeys.some((k) => draftSelected.has(k));

  const sortAsc = () => {
    column.toggleSorting?.(false);
    closePanel();
  };
  const sortDesc = () => {
    column.toggleSorting?.(true);
    closePanel();
  };

  const inputType = kind === 'date' ? 'date' : kind === 'number' ? 'text' : 'text';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title="필터"
        aria-label="열 필터"
        aria-expanded={open}
        onClick={openPanel}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          flexShrink: 0,
          width: 18,
          height: 18,
          padding: 0,
          border: 'none',
          borderRadius: 2,
          background:
            iconTone === 'onAccent'
              ? (isFiltered ? 'rgba(255,255,255,0.28)' : 'transparent')
              : (isFiltered ? theme.colors.primaryLight : 'transparent'),
          color:
            iconTone === 'onAccent'
              ? theme.colors.white
              : (isFiltered ? theme.colors.primary : theme.colors.text.tertiary),
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="열 필터"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: PANEL_WIDTH,
              maxHeight: PANEL_MAX_HEIGHT,
              zIndex: 10000,
              background: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius?.sm || 4,
              boxShadow: theme.shadows.lg,
              display: 'flex',
              flexDirection: 'column',
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.primary,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', borderBottom: `1px solid ${theme.colors.border}` }}>
              <TabBtn active={tab === 'list'} onClick={() => setTab('list')}>
                값
              </TabBtn>
              <TabBtn active={tab === 'cond'} onClick={() => setTab('cond')}>
                조건
              </TabBtn>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                padding: '4px 10px 6px',
                borderBottom: `1px solid ${theme.colors.borderLight || theme.colors.border}`,
                fontSize: theme.typography.fontSize.xs,
              }}
            >
              <button type="button" onClick={sortAsc} style={linkBtnStyle}>
                오름차순
              </button>
              <button type="button" onClick={sortDesc} style={linkBtnStyle}>
                내림차순
              </button>
            </div>
            {tab === 'list' ? (
              <>
                <div style={{ padding: '8px 8px 4px' }}>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="목록에서 검색..."
                    autoFocus
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '4px 10px 6px',
                    fontSize: theme.typography.fontSize.xs,
                  }}
                >
                  <button type="button" onClick={selectAllVisible} style={linkBtnStyle}>
                    전체선택
                  </button>
                  <button type="button" onClick={clearVisible} style={linkBtnStyle}>
                    선택해제
                  </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', minHeight: 80 }}>
                  {visibleKeys.length === 0 ? (
                    <div style={{ padding: '12px 10px', color: theme.colors.text.tertiary }}>항목 없음</div>
                  ) : (
                    <>
                      <label style={checkRowStyle}>
                        <input
                          type="checkbox"
                          checked={allVisibleChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = !allVisibleChecked && someVisibleChecked;
                          }}
                          onChange={() => (allVisibleChecked ? clearVisible() : selectAllVisible())}
                        />
                        <span>(모두 선택)</span>
                      </label>
                      {visibleKeys.map((key) => (
                        <label key={key} style={checkRowStyle} title={key}>
                          <input
                            type="checkbox"
                            checked={draftSelected.has(key)}
                            onChange={() => toggleOne(key)}
                          />
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontStyle: key === EMPTY_VALUE_KEY ? 'italic' : undefined,
                              color:
                                key === EMPTY_VALUE_KEY
                                  ? theme.colors.text.tertiary
                                  : theme.colors.text.primary,
                            }}
                          >
                            {key}
                          </span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
                <div style={footerStyle}>
                  <button type="button" onClick={clearFilter} style={secondaryBtnStyle}>
                    필터 해제
                  </button>
                  <button type="button" onClick={applyList} style={primaryBtnStyle}>
                    확인
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
                  <select
                    value={draftOp}
                    onChange={(e) => setDraftOp(e.target.value)}
                    style={inputStyle}
                  >
                    {ops.map((op) => (
                      <option key={op} value={op}>
                        {OP_LABEL[op] || op}
                      </option>
                    ))}
                  </select>
                  {draftOp !== 'empty' && draftOp !== 'notEmpty' && (
                    <input
                      type={inputType}
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      placeholder={kind === 'number' ? '금액/숫자' : '값'}
                      style={inputStyle}
                    />
                  )}
                  {draftOp === 'between' && (
                    <input
                      type={inputType}
                      value={draftValue2}
                      onChange={(e) => setDraftValue2(e.target.value)}
                      placeholder="끝 값"
                      style={inputStyle}
                    />
                  )}
                  <label style={checkRowStyle}>
                    <input
                      type="checkbox"
                      checked={draftHide}
                      onChange={(e) => setDraftHide(e.target.checked)}
                    />
                    조건에 맞는 행만 표시 (필터)
                  </label>
                  <div>
                    <div style={{ marginBottom: 4, fontSize: theme.typography.fontSize.xs }}>셀 색상</div>
                    <ColorRow value={draftCellColor} onChange={setDraftCellColor} />
                  </div>
                  <div>
                    <div style={{ marginBottom: 4, fontSize: theme.typography.fontSize.xs }}>행 색상</div>
                    <ColorRow value={draftRowColor} onChange={setDraftRowColor} />
                  </div>
                  <label style={checkRowStyle}>
                    <input
                      type="checkbox"
                      checked={draftPersist}
                      onChange={(e) => setDraftPersist(e.target.checked)}
                    />
                    저장하고 유지 (다음에도 적용)
                  </label>
                  {!draftHide && !draftCellColor && !draftRowColor && (
                    <div style={{ color: theme.colors.status.warning, fontSize: theme.typography.fontSize.xs }}>
                      필터·셀 색·행 색 중 하나 이상을 선택하세요.
                    </div>
                  )}
                </div>
                <div style={footerStyle}>
                  <button type="button" onClick={clearFilter} style={secondaryBtnStyle}>
                    조건 해제
                  </button>
                  <button type="button" onClick={applyCondition} style={primaryBtnStyle}>
                    확인
                  </button>
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '6px 0',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontWeight: active ? 700 : 400,
        color: active ? theme.colors.primary : theme.colors.text.secondary,
        borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
      }}
    >
      {children}
    </button>
  );
}

function ColorRow({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      <button
        type="button"
        title="없음"
        onClick={() => onChange(null)}
        style={{
          width: 16,
          height: 16,
          borderRadius: 3,
          border: `1px solid ${theme.colors.border}`,
          background: theme.colors.white,
          cursor: 'pointer',
          outline: value == null ? `2px solid ${theme.colors.primary}` : 'none',
        }}
      />
      {HIGHLIGHT_PALETTE.map((p) => {
        const raw = theme.colors[p.token];
        return (
          <button
            key={p.id}
            type="button"
            title={p.label}
            onClick={() => onChange(p.id)}
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              border: `1px solid ${theme.colors.border}`,
              background: raw,
              cursor: 'pointer',
              outline: value === p.id ? `2px solid ${theme.colors.primary}` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

const linkBtnStyle = {
  border: 'none',
  background: 'none',
  color: theme.colors.primary,
  cursor: 'pointer',
  padding: 0,
  fontSize: 'inherit',
};

const checkRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 0',
  cursor: 'pointer',
  userSelect: 'none',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '5px 8px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 4,
  fontSize: theme.typography.fontSize.sm,
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 6,
  padding: 8,
  borderTop: `1px solid ${theme.colors.border}`,
};

const secondaryBtnStyle = {
  padding: '4px 10px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 4,
  background: theme.colors.white,
  cursor: 'pointer',
  fontSize: theme.typography.fontSize.xs,
};

const primaryBtnStyle = {
  padding: '4px 12px',
  border: 'none',
  borderRadius: 4,
  background: theme.colors.primary,
  color: theme.colors.white,
  cursor: 'pointer',
  fontSize: theme.typography.fontSize.xs,
  fontWeight: 600,
};

export default ExcelColumnFilterButton;
